import { useEffect, useState } from "react"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import {
  ACTIVE_JOB_STATUSES,
  JobRunStatus,
  useGetJobQuery,
} from "~/ui-rtk/api/jobsApi"
import {
  JobResultRows,
  describeJob,
  getJobLabel,
} from "~/ui-rtk/pages/Jobs/jobViews"

const STATUS_BADGE: Record<JobRunStatus, { label: string; cls: string }> = {
  AWAITING_APPROVAL: {
    label: "Awaiting approval",
    cls: "bg-amber-500/20 text-amber-300",
  },
  SCHEDULED: { label: "Scheduled", cls: "bg-sky-500/20 text-sky-300" },
  PENDING: { label: "Queued", cls: "bg-sky-500/20 text-sky-300" },
  PROCESSING: { label: "In progress", cls: "bg-amber-500/20 text-amber-300" },
  COMPLETED: { label: "Completed", cls: "bg-emerald-500/20 text-emerald-300" },
  FAILED: { label: "Failed", cls: "bg-rose-500/20 text-rose-300" },
  REJECTED: { label: "Rejected", cls: "bg-rose-500/20 text-rose-300" },
  CANCELLED: {
    label: "Cancelled",
    cls: "bg-system-gray-500/20 text-system-gray-300",
  },
}

function fmtDate(iso?: string | null): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/** "3/8" → percentage for the progress bar. */
function stepPercent(step: string | null): number | undefined {
  if (!step || !step.includes("/")) return undefined
  const [a, b] = step.split("/").map(s => Number(s.trim()))
  if (Number.isFinite(a) && Number.isFinite(b) && b > 0) {
    return Math.round((a / b) * 100)
  }
  return undefined
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: string
}) {
  return (
    <div className="flex-1 rounded-lg bg-white/5 px-3 py-2">
      <div className={`text-lg font-bold ${tone ?? "text-white"}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
    </div>
  )
}

export function JobDetailModal({
  jobId,
  onClose,
}: {
  jobId: string
  onClose: () => void
}) {
  // Poll while the job is active, stop once it reaches a terminal state.
  const [pollMs, setPollMs] = useState(4000)
  const {
    data: job,
    isLoading,
    isError,
  } = useGetJobQuery(jobId, { pollingInterval: pollMs })

  useEffect(() => {
    if (job && !ACTIVE_JOB_STATUSES.includes(job.status)) setPollMs(0)
  }, [job])

  const badge = job ? STATUS_BADGE[job.status] : undefined
  const percent = stepPercent(job?.step ?? null)
  const isActive = job ? ACTIVE_JOB_STATUSES.includes(job.status) : false
  const summary = job?.result?.summary
  const subtitle = job ? describeJob(job) : null

  return (
    <Dialog
      isOpen
      handleClose={onClose}
      closeOnClickOutside
      width={640}
      classes={{
        container:
          "bg-system-blue-800 border-2 border-system-blue-700 rounded-2xl p-0 flex flex-col max-w-2xl w-full max-h-[85vh] overflow-hidden text-left",
      }}
    >
      <div className="flex items-center justify-between gap-4 border-b border-white/10 p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">
              {job ? getJobLabel(job.type) : "Job"}
            </h2>
            {badge && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}
              >
                {badge.label}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="mt-0.5 truncate text-xs text-system-gray-400">
              {subtitle}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 transition-colors hover:text-white"
          aria-label="Close"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {isLoading && <p className="text-sm text-system-gray-400">Loading…</p>}
        {isError && (
          <p className="text-sm text-rose-300">
            Couldn&apos;t load this job. It may have expired.
          </p>
        )}

        {job && (
          <>
            {/* Progress (while running) */}
            {isActive && (
              <div className="mb-4">
                <div className="mb-1 flex items-center justify-between text-xs text-system-gray-400">
                  <span>Working…</span>
                  {job.step && <span className="font-mono">{job.step}</span>}
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-amber-400 transition-all"
                    style={{
                      width: percent !== undefined ? `${percent}%` : "15%",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Failure */}
            {job.status === "FAILED" && job.error && (
              <div className="mb-4 rounded-lg border border-rose-500/40 bg-rose-900/20 p-3 text-sm text-rose-200">
                {job.error}
              </div>
            )}

            {/* Summary counts */}
            {summary && (
              <div className="mb-4 flex gap-3">
                <Stat label="Total" value={summary.total} />
                <Stat
                  label="Succeeded"
                  value={summary.successful}
                  tone="text-emerald-400"
                />
                <Stat
                  label="Failed"
                  value={summary.failed}
                  tone={summary.failed ? "text-rose-400" : undefined}
                />
              </div>
            )}

            {/* Scheduled run-at (one-shot future blasts) */}
            {job.status === "SCHEDULED" && job.scheduledFor && (
              <div className="mb-4 rounded-lg bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
                Scheduled to run at{" "}
                <span className="font-medium">{fmtDate(job.scheduledFor)}</span>
              </div>
            )}

            {/* Timeline */}
            <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-white/5 px-3 py-2 text-[11px]">
              <div>
                <div className="text-system-gray-500">Enqueued</div>
                <div className="text-system-gray-200">
                  {fmtDate(job.enqueuedAt) ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-system-gray-500">Started</div>
                <div className="text-system-gray-200">
                  {fmtDate(job.processingStartedAt) ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-system-gray-500">Completed</div>
                <div className="text-system-gray-200">
                  {fmtDate(job.completedAt) ?? "—"}
                </div>
              </div>
            </div>

            {/* Per-type result rows */}
            {!isActive && <JobResultRows job={job} />}
          </>
        )}
      </div>
    </Dialog>
  )
}
